import { setGlobalError } from '@/components/ShowGlobalError/Slice/ShowGlobalErrorSlice';
import { isRejectedWithValue } from '@reduxjs/toolkit';
import type { MiddlewareAPI, Middleware } from '@reduxjs/toolkit';
import configuredStore from './Store';
import { captureError } from '@/helpers/captureError';
import { normalizeApiError } from '@/helpers/apiError/normalizeApiError';

const SILENT_ENDPOINTS = new Set([
  "downloadFile",
  "allBarcodesPrinted",
]);

const getBodyAsString = async (action: any): Promise<string | undefined> => {
  const body = action?.meta?.baseQueryMeta?.request?.body;

  if (!body) return undefined;

  if (body instanceof ReadableStream) {
    try {
      const reader = body.getReader();
      let result = '';
      const decoder = new TextDecoder();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        result += decoder.decode(value, { stream: true });
      }
      result += decoder.decode();
      return result;
    } catch (error) {
      console.error('Ошибка при чтении ReadableStream:', error);
      return undefined;
    }
  }

  if (typeof body === 'object' && !(body instanceof FormData)) {
    return JSON.stringify(body);
  }

  if (typeof body === 'string') return body;

  return String(body);
};

export const rtkQueryErrorLogger: Middleware =
  (api: MiddlewareAPI) => (next) => async (action) => {
    if (!isRejectedWithValue(action)) {
      return next(action);
    }

    const payload = action.payload;
    const status = payload?.status;
    const endpointName = action.meta?.arg?.endpointName;
    const requestMeta = action.meta?.baseQueryMeta?.request ?? action.meta?.request;
    const method: string | undefined = requestMeta?.method;
    const url: string | undefined = requestMeta?.url;

    // Тихо пропускаем: 400/422 (validation формы — обрабатывается компонентами в catch)
    if (status === 400 || status === 422) {
      return next(action);
    }

    if (status === 401 && typeof url === 'string' && /get-current-user/i.test(url)) {
      return next(action);
    }

    // Эндпоинты с собственной обработкой ошибок — не показываем глобальный попап
    if (endpointName && SILENT_ENDPOINTS.has(endpointName)) {
      return next(action);
    }

    // Нормализуем ошибку
    const normalized = normalizeApiError(payload, { method, url });

    // 401/403 — показываем предупреждение, но не шлём в Sentry
    if (normalized.type === "authorization" || normalized.type === "forbidden") {
      api.dispatch(setGlobalError(normalized));
      return next(action);
    }

    // Для остальных — диспатчим в UI
    api.dispatch(setGlobalError(normalized));

    // Отправка в Sentry/captureError
    const state = configuredStore.getState();
    const user = state.currentUser.user;

    if (action.type === "api/executeQuery/rejected") {
      captureError({
        originalError: payload?.data ?? payload,
        user,
        http: { url, method, payload },
        state,
      });
    }

    if (action.type === "api/executeMutation/rejected") {
      const requestBody = await getBodyAsString(action);
      captureError({
        originalError: payload?.data ?? payload,
        user,
        http: { url, method, payload: requestBody },
        state,
      });
    }

    return next(action);
  };